import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RecentDeals } from "./components/recent-deals";
import { RecentSales } from "./components/recent-sales";
import { getCurrencyFormatted } from "@/lib/app";
import { useAppStore } from "@/stores";
import {
  filterDeals,
  getDealsValue,
} from "@revelate/calc/commission";
import { NestedDeal, NestedUser, SignedInCompany } from "@revelate/types";
import { getDateRangePreset } from "@revelate/utils";
import React from "react";

interface RecentDealCardProps {
  company: SignedInCompany | undefined;
  deals: NestedDeal[];
}

export const RecentDealsCard = ({ company, deals }: RecentDealCardProps) => {
  const { scope, dateRange, companyCurrency } = useAppStore((state) => state);

  if (!company) return null;

  const scopedDeals = filterDeals({
    deals: deals || [],
    dateRange,
    scope,
  }) as NestedDeal[];

  const scopedDealsWon = scopedDeals.filter((deal) => deal.is_won);

  return (
    <Card className="col-span-3">
      <CardHeader>
        <CardTitle>Based on {scopedDealsWon.length} deals</CardTitle>
        <CardDescription>
          {scope?.label.split(" ")[0]}'s won deal value this period is{" "}
          {getCurrencyFormatted(
            getDealsValue(company, scopedDealsWon),
            companyCurrency?.code
          )}
          .
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RecentDeals deals={scopedDealsWon} />
      </CardContent>
    </Card>
  );
};

export const RecentSalesCard = ({
  scopedUsers,
  projectedUsers,
}: {
  scopedUsers: NestedUser[];
  projectedUsers: NestedUser[];
}) => {
  const { dateRange } = useAppStore((state) => state);

  const getMatchingPresetValue = React.useCallback(() => {
    if (!dateRange?.from || !dateRange?.to) return "";
    // Find matching preset
    const matchingPreset = getDateRangePreset(dateRange);
    return matchingPreset
      ? `${matchingPreset.label.toLowerCase()}`
      : "this period";
  }, [dateRange]);

  return (
    <Card className="col-span-3">
      <CardHeader>
        <CardTitle>
          <h2>
            Commission per employee
          </h2>
        </CardTitle>
        <CardDescription>
          The total commission earned {getMatchingPresetValue().toLowerCase()} by each employee
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RecentSales
          scopedUsers={scopedUsers}
          projectedUsers={projectedUsers}
        />
      </CardContent>
    </Card>
  );
};
